// /settings/billing — credit balance, ledger, pricing. V0 placeholder.
// Stripe checkout CTAs are disabled pending Sprint 11 (M3 monetization).
// Internal team (billing_exempt) sees everything but with a banner noting
// that credit spend is recorded, not deducted.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Infinity as InfinityIcon,
  Receipt,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Masthead } from '@/components/Masthead';
import { SkeletonLine } from '@/components/Skeleton';
import type { PricingRow } from '@/lib/api-types';

const ACTION_LABEL: Record<string, string> = {
  rlp_extraction: 'Extract RLP',
  marketplace_scan: 'Marketplace scan',
  contact_unlock: 'Contact unlock',
  offer_package: 'Offer-builder package',
  qa_question: 'Q&A question',
};

const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  solo_pro: 'Solo Pro',
  team: 'Team',
  enterprise: 'Enterprise',
};

function formatTs(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function kindTone(kind: string): string {
  if (kind === 'spend') return 'text-ink';
  if (kind === 'refund') return 'text-warn-bold';
  if (kind.startsWith('grant_')) return 'text-ok-bold';
  return 'text-ink-muted';
}

function StatCard({
  label,
  icon,
  hint,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card flex flex-col gap-3 p-5">
      <div className="flex items-start justify-between">
        <div className="text-[0.8125rem] font-medium text-ink-muted">{label}</div>
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
          {icon}
        </div>
      </div>
      <div className="text-[1.5rem] font-bold tracking-head text-ink tabular-nums">
        {children}
      </div>
      {hint && <div className="text-[0.8125rem] text-ink-muted">{hint}</div>}
    </div>
  );
}

export default function Billing() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 30_000 });
  const credits = useQuery({
    queryKey: ['credits'],
    queryFn: () => api.credits(50),
    refetchOnWindowFocus: true,
  });
  const pricing = useQuery({
    queryKey: ['credits-pricing'],
    queryFn: api.creditsPricing,
    staleTime: 5 * 60 * 1000,
  });

  const exempt = me.data?.workspace.billing_exempt ?? false;
  const plan = me.data?.workspace.plan ?? 'free';
  const balance = credits.data?.balance ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <Masthead
        kicker="Account"
        title="Billing"
        lede="Plan, credits, and transaction history. Stripe checkout opens in M3 (Sprint 11)."
      />

      {exempt && (
        <div className="rounded-xl border border-info-border bg-info-soft px-4 py-3 text-[0.8125rem] text-info-bold">
          This is a billing-exempt workspace (internal team). Credit spend is
          recorded for telemetry but balance is never decremented.
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label="Credit balance"
          icon={<Sparkles className="h-4 w-4" />}
          hint={exempt ? 'billing-exempt workspace' : 'available for paid actions'}
        >
          {credits.isLoading ? (
            <SkeletonLine width="5rem" />
          ) : exempt ? (
            <span className="inline-flex items-center gap-1">
              <InfinityIcon className="h-6 w-6" /> Unlimited
            </span>
          ) : (
            `${balance} cr`
          )}
        </StatCard>
        <StatCard
          label="Plan"
          icon={<Wallet className="h-4 w-4" />}
          hint={me.data?.workspace.billing_cycle ?? '—'}
        >
          {PLAN_LABEL[plan] ?? plan}
        </StatCard>
        <StatCard
          label="Lifetime spent"
          icon={<Receipt className="h-4 w-4" />}
          hint={`${credits.data?.granted_lifetime ?? 0} cr granted`}
        >
          {credits.isLoading ? '—' : `${credits.data?.spent_lifetime ?? 0} cr`}
        </StatCard>
      </div>

      {/* Pricing table */}
      <section className="rounded-xl border border-rule bg-surface p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[0.9375rem] font-semibold text-ink">Per-action pricing</h2>
          <Link
            to="/settings/ledger"
            className="inline-flex items-center gap-1 text-[0.8125rem] text-brand-500 hover:underline"
          >
            Agent cost ledger <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {pricing.isLoading ? (
          <SkeletonLine />
        ) : (
          <div className="overflow-hidden rounded-lg border border-rule">
            <table className="w-full text-[0.8125rem]">
              <thead className="bg-surface-sunk text-ink-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Action</th>
                  <th className="px-3 py-2 text-right font-semibold">Cost</th>
                  <th className="px-3 py-2 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {(pricing.data?.pricing ?? []).map((p: PricingRow) => (
                  <tr key={p.action_key} className="border-t border-rule">
                    <td className="px-3 py-2 font-medium text-ink">
                      {ACTION_LABEL[p.action_key] ?? p.action_key}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {p.credit_cost} cr
                    </td>
                    <td className="px-3 py-2 text-ink-muted">{p.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent transactions */}
      <section className="rounded-xl border border-rule bg-surface p-5">
        <h2 className="mb-3 text-[0.9375rem] font-semibold text-ink">Recent transactions</h2>
        {credits.isLoading ? (
          <SkeletonLine />
        ) : credits.data && credits.data.transactions.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-rule">
            <table className="w-full text-[0.8125rem]">
              <thead className="bg-surface-sunk text-ink-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">When</th>
                  <th className="px-3 py-2 text-left font-semibold">Kind</th>
                  <th className="px-3 py-2 text-right font-semibold">Amount</th>
                  <th className="px-3 py-2 text-left font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {credits.data.transactions.map((t) => (
                  <tr key={t.id} className="border-t border-rule">
                    <td className="px-3 py-2 text-ink-muted">{formatTs(t.created_at)}</td>
                    <td className={`px-3 py-2 font-medium ${kindTone(t.kind)}`}>
                      {t.kind}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {t.amount > 0 ? `+${t.amount}` : t.amount}
                    </td>
                    <td className="px-3 py-2 text-ink-muted">{t.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-rule px-4 py-6 text-center text-[0.8125rem] text-ink-muted">
            No credit transactions yet. Spend or grant events will show up here.
          </div>
        )}
      </section>
    </div>
  );
}
